/**
 * Field-level diff between two JSON documents, used by the change-history
 * diff viewer (ZEN-719) to show reviewers exactly which paths changed
 * between two target values.
 */

// Caps the number of reported changes for a single diff — a pathological
// document (very wide array/object) could otherwise produce an unbounded
// list and freeze the diff view.
export const JSON_DIFF_CHANGE_LIMIT = 200

function isPlainObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function sameType(a, b) {
    if (isPlainObject(a)) return isPlainObject(b)
    if (Array.isArray(a)) return Array.isArray(b)
    return typeof a === typeof b
}

/**
 * Walks two decoded JSON values in parallel, pushing a change entry for
 * every path that was added, removed, or whose leaf value changed.
 *
 * Arrays are compared index-by-index rather than with a sequence/LCS diff,
 * so an insertion/removal in the middle of an array reports every
 * downstream index as "changed" rather than detecting the shift — an
 * acceptable simplification for the flag values this diffs (small
 * configuration documents, not long lists).
 */
function walk(oldValue, newValue, path, changes) {
    if (changes.length >= JSON_DIFF_CHANGE_LIMIT) {
        return
    }

    if (isPlainObject(oldValue) && isPlainObject(newValue)) {
        // Excludes __proto__/constructor/prototype: JSON.parse() itself never
        // lets a decoded key touch the real prototype chain (it always
        // creates ordinary own properties), but skipping these names is a
        // cheap extra guard against ever treating one as a real diff path.
        const keys = new Set(
            [...Object.keys(oldValue), ...Object.keys(newValue)].filter(
                (key) =>
                    key !== '__proto__' &&
                    key !== 'constructor' &&
                    key !== 'prototype'
            )
        )

        for (const key of keys) {
            if (changes.length >= JSON_DIFF_CHANGE_LIMIT) {
                return
            }

            const childPath = path === '' ? key : `${path}.${key}`
            const hasOld = Object.prototype.hasOwnProperty.call(oldValue, key)
            const hasNew = Object.prototype.hasOwnProperty.call(newValue, key)

            if (!hasOld) {
                changes.push({
                    path: childPath,
                    type: 'added',
                    // eslint-disable-next-line security/detect-object-injection -- key is one of this object's own enumerated keys (Object.keys(), filtered above), never attacker-chosen input.
                    newValue: newValue[key],
                })
            } else if (!hasNew) {
                changes.push({
                    path: childPath,
                    type: 'removed',
                    // eslint-disable-next-line security/detect-object-injection -- key is one of this object's own enumerated keys (Object.keys(), filtered above), never attacker-chosen input.
                    oldValue: oldValue[key],
                })
            } else {
                // eslint-disable-next-line security/detect-object-injection -- key is one of this object's own enumerated keys (Object.keys(), filtered above), never attacker-chosen input.
                walk(oldValue[key], newValue[key], childPath, changes)
            }
        }

        return
    }

    if (Array.isArray(oldValue) && Array.isArray(newValue)) {
        const length = Math.max(oldValue.length, newValue.length)

        for (let i = 0; i < length; i++) {
            if (changes.length >= JSON_DIFF_CHANGE_LIMIT) {
                return
            }

            const childPath = `${path}[${i}]`

            if (i >= oldValue.length) {
                changes.push({
                    path: childPath,
                    type: 'added',
                    // eslint-disable-next-line security/detect-object-injection -- i is a bounded loop counter (0..length), never attacker-chosen input.
                    newValue: newValue[i],
                })
            } else if (i >= newValue.length) {
                changes.push({
                    path: childPath,
                    type: 'removed',
                    // eslint-disable-next-line security/detect-object-injection -- i is a bounded loop counter (0..length), never attacker-chosen input.
                    oldValue: oldValue[i],
                })
            } else {
                // eslint-disable-next-line security/detect-object-injection -- i is a bounded loop counter (0..length), never attacker-chosen input.
                walk(oldValue[i], newValue[i], childPath, changes)
            }
        }

        return
    }

    if (!sameType(oldValue, newValue) || oldValue !== newValue) {
        changes.push({
            path: path === '' ? '(root)' : path,
            type: 'changed',
            oldValue,
            newValue,
        })
    }
}

/**
 * Diff two JSON display strings (as stored on a target value's
 * `value.data.display`), returning the list of changed paths.
 *
 * `oldDisplay` may be `null`/`undefined` to mean "no prior version" — every
 * path in `newDisplay` is then reported as added, rather than diffed
 * against nothing. `error` is set instead of `changes` when either display
 * isn't parseable JSON, which callers should render as a fallback message
 * rather than an empty diff.
 *
 * @param {string|null|undefined} oldDisplay
 * @param {string|null|undefined} newDisplay
 * @returns {{changes: Array|null, error: 'invalid-old'|'invalid-new'|null, truncated: boolean}}
 */
export function diffJson(oldDisplay, newDisplay) {
    if (newDisplay === null || newDisplay === undefined) {
        return { changes: [], error: null, truncated: false }
    }

    let newValue
    try {
        newValue = JSON.parse(newDisplay)
    } catch {
        return { changes: null, error: 'invalid-new', truncated: false }
    }

    if (oldDisplay === null || oldDisplay === undefined) {
        if (!isPlainObject(newValue) && !Array.isArray(newValue)) {
            return {
                changes: [{ path: '(root)', type: 'added', newValue }],
                error: null,
                truncated: false,
            }
        }

        const changes = []
        walk(Array.isArray(newValue) ? [] : {}, newValue, '', changes)
        return {
            changes,
            error: null,
            truncated: changes.length >= JSON_DIFF_CHANGE_LIMIT,
        }
    }

    let oldValue
    try {
        oldValue = JSON.parse(oldDisplay)
    } catch {
        return { changes: null, error: 'invalid-old', truncated: false }
    }

    const changes = []
    walk(oldValue, newValue, '', changes)
    return {
        changes,
        error: null,
        truncated: changes.length >= JSON_DIFF_CHANGE_LIMIT,
    }
}
